import React, { useState, useEffect, useContext, useMemo, useRef } from "react";

import { UserContext } from "shared/context/UserContext";
import { DateRangeForm } from "./DateRangeForm";
import { Row, Col, Button, Container } from "react-bootstrap";
import ErrorHandler from "../../../components/core/ErrorHandler";
import { DetailsModal } from "../../../components/grid/DetailsModal";
import Accordion from "../../../components/Accordion";
import { IgnoreModal } from "shared/components/ignoreModal/IgnoreModal";
import { IgnoreBody } from "shared/components/ignoreModal/IgnoreBody";

import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import SharedMainSelectableServerGrid from "shared/components/grids/serverGrid/SharedMainSelectableServerGrid";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";
import { useInsertServerSideExport } from "shared/hooks/useInsertServerSideExport";

import ShouldDisplayDetailsModal from "./ShouldDisplayDetailsModalHelper";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { getter, ZIndexContext } from "@progress/kendo-react-common";

import {
  MoneyCell,
  AggregateMoneyFooterCell,
  AggregateNumberFooterCell,
} from "../../../components/grid/Kendo/CustomKendoGridCells";

import {
  DefaultColumnWidth,
  DefaultDateColumnWidth,
  DefaultBooleanOrShortWidthColumnWidth,
} from "../../../components/grid/Kendo/KendoGridAndColumnConstants";

import { PromiseToPayHeaderCell } from "./components/PromiseToPayHeaderCell";

import useGridDataStateResetProcessing from "shared/hooks/useGridDataStateResetProcessing";
import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";

import useGridButtons from "shared/components/grids/hooks/useGridButtons";
import { SetSummaryDetailsDataHelper } from "./helpers/SetSummaryDetailsDataHelper";

export function DateRange(props) {
  const { invalidateReactQueryKey } = useReactQueryHelpers();

  const DETAILS_SELECTED_FIELD = "SelectedClaimId";
  const DETAILS_DATA_ITEM_KEY = "claimId";
  const idGetter = getter(DETAILS_DATA_ITEM_KEY);

  const { passedProps } = props;
  const userContext = useContext(UserContext);

  const user = userContext.email;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState({});

  const [submittedFormData, setSubmittedFormData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showIgnoreCount, setShowIgnoreCount] = useState(false);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [ignoreError, setIgnoreError] = useState({});
  const [refreshDetailGrid, setRefreshDetailGrid] = useState(false);

  const [mainGridAggregates, setMainGridAggregates] = useState([]);

  const [detailError, setDetailError] = useState({});
  const [detailsGridTitle, setDetailsGridTitle] = useState("");
  const [summaryDetailsGridData, setSummaryDetailsGridData] = useState(null);

  //NOTE TODO: This is an example of something that should be handled by the shared serverside grid
  const [selectedDetailsState, setSelectedDetailsState] = useState({});

  const [selectedNcpdpIgnoreModalText, setSelectedNcpdpIgnoreModalText] =
    useState("");
  const [selectedRxIgnoreModalText, setSelectedRxIgnoreModalText] =
    useState("");
  const [selectedClid, setSelectedClid] = useState("");
  const [selectedDetailsProps, setSelectedDetailsProps] = useState("");
  const [
    ignoreMultipleSelectedClaimsInDetailModal,
    setIgnoreMultipleSelectedClaimsInDetailModal,
  ] = useState(false);

  const [detailsGridParameters, setDetailsGridParameters] = useState({});
  const [showFilter, setShowFilter] = useState(""); // IRA-38: Empty string for "Show All"

  const sendAggregatesToParent = (aggregatesFromSharedGrid) => {
    setMainGridAggregates(aggregatesFromSharedGrid.data);
  };

  const {
    gridDataStateResetProcessingInitiated,
    InitiateGridDataStateReset,
    GridDataStateResetCompleted: onGridDataStateResetCompleted,
  } = useGridDataStateResetProcessing();

  const _container = React.useRef();
  const agingDetailsServerGridRef = React.useRef();

  const { AddGridButton, gridButtons } = useGridButtons();

  const clientGridRef = useRef();

  function NewDetailSelectionChange(event, checkedId) {
    // initilize a new dictionary
    let newSelectedState = {};

    let checked = event.target.checked;

    // if the checkedId is not in the selectedDetailsState, then add it
    if (!selectedDetailsState[checkedId]) {
      newSelectedState = {
        ...selectedDetailsState,
        [checkedId]: checked,
      };
    } else {
      // if the checkedId is in the selectedDetailsState, then remove it
      newSelectedState = { ...selectedDetailsState };
      delete newSelectedState[checkedId];
    }

    setSelectedDetailsState(newSelectedState);
  }

  function getDateRangeDetails(props) {
    try {
      setDetailsGridTitle(props.dataItem["Payer"] + " Total Outstanding");

      let parameters1 = {
        user: user,
        mmids: submittedFormData.mmid,
        AgeAsOf: null,
        PBID: JSON.stringify(props.dataItem["PBID"]),
        fromDate: submittedFormData.fromDate,
        toDate: submittedFormData.toDate,
        dateType: submittedFormData.dateType,
        outstandingType: submittedFormData.reportType,
        payer: props.dataItem["Payer"],
        isAggregate: false,
      };

      setDetailsGridParameters(parameters1);

      setShowModal(true);
      setDetailError({});

      return;
    } catch (exception) {
      onFailure(exception);
    }
  }

  function getAggregateDateRangeDetails() {
    try {
      setDetailsGridTitle("Total Outstanding Aggregate");

      // Get all filtered data from all pages (respects current filters but includes all pages)
      const mainGridData = clientGridRef.current
        ? clientGridRef.current.GetAllFilteredData()
        : [];

      let pushPBID = [];

      for (var count = 0; count < mainGridData.length; count++) {
        pushPBID.push(mainGridData[count].PBID);
      }

      // Check if there is any data to display
      if (pushPBID.length === 0) {
        alert("No data is displayed on grid");
        return;
      }

      let parameters = {
        user: user,
        mmids: submittedFormData.mmid,
        AgeAsOf: null,
        PBID: JSON.stringify(pushPBID).replace("[", "").replace("]", ""),
        fromDate: submittedFormData.fromDate,
        toDate: submittedFormData.toDate,
        dateType: submittedFormData.dateType,
        outstandingType: submittedFormData.reportType,
        isAggregate: true,
      };

      setDetailsGridParameters(parameters);

      setShowModal(true);
      setDetailError({});
    } catch (exception) {
      onFailure(exception);
    }
  }

  const RxCountCell = (props) => {
    if (submittedFormData.reportType === "pending") {
      let onCellClick = () => {
        setSelectedDetailsProps(props);
        getDateRangeDetails(props);
      };

      return (
        <td>
          <a
            onClick={onCellClick}
            style={{
              color: "#015CAB",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {props.children}
          </a>
        </td>
      );
    } else {
      return (
        <td>
          <a>{props.children}</a>
        </td>
      );
    }
  };

  const TotalOutstandingMoneyCell = (props) => {
    if (submittedFormData.reportType === "pending") {
      return MoneyCell(props);
    } else {
      let onCellClick = () => {
        setSelectedDetailsProps(props);
        getDateRangeDetails(props);
      };

      let options = {
        onClick: onCellClick,
      };

      return MoneyCell(props, options);
    }
  };

  const AggregateMainGridMoneyFooterCell = (props) => {
    var options = {
      aggregateData: mainGridAggregates,
    };

    return AggregateMoneyFooterCell(props, options);
  };

  const AggregateMainGridMoneyFooterCellWithEvent = (props) => {
    let onCellClick = () => {
      getAggregateDateRangeDetails();
    };

    var options = {
      aggregateData: mainGridAggregates,
      onClick: onCellClick,
    };

    return AggregateMoneyFooterCell(props, options);
  };

  const AggregateMainGridNumberFooterCell = (props) => {
    var options = {
      aggregateData: mainGridAggregates,
    };

    return AggregateNumberFooterCell(props, options);
  };

  // const AggregateMoneyDetailFooterCell = (props) => {
  //   let displayAggregateValue = 0;

  //   if (!agingDetailsServerGridRef.current) {
  //     // return an empty cell

  //     return (
  //       <td
  //         aria-colindex={props.tdProps["aria-colindex"]}
  //         className={props.tdProps.className}
  //         colSpan={props.tdProps.colSpan}
  //         role={props.tdProps.role}
  //         style={props.tdProps.style}
  //         key={props.tdProps.key}
  //       >
  //         {"$" + displayAggregateValue}
  //       </td>
  //     );
  //   }

  //   const gridData = agingDetailsServerGridRef.current
  //     ? agingDetailsServerGridRef.current.GetAllGridData()
  //     : null;

  //   if (gridData && gridData.fieldTotals) {
  //     let fieldTotals = gridData.fieldTotals;
  //     let field = props.field.toLowerCase();
  //     let fieldTotal = fieldTotals[field];
  //     if (fieldTotal) {
  //       displayAggregateValue = fieldTotal;
  //     }
  //   }

  //   return (
  //     <td
  //       aria-colindex={props.tdProps["aria-colindex"]}
  //       className={props.tdProps.className}
  //       colSpan={props.tdProps.colSpan}
  //       role={props.tdProps.role}
  //       style={props.tdProps.style}
  //       key={props.tdProps.key}
  //     >
  //       {"$" + displayAggregateValue}
  //     </td>
  //   );
  // };

  const AggregateMoneyDetailFooterCell = (props) => {
    if (!agingDetailsServerGridRef.current) {
      return (
        <td
          aria-colindex={props.tdProps["aria-colindex"]}
          className={props.tdProps.className}
          colSpan={props.tdProps.colSpan}
          role={props.tdProps.role}
          style={props.tdProps.style}
          key={props.tdProps.key}
        >
          {"$0.00"}
        </td>
      );
    } else {
      const gridData = agingDetailsServerGridRef.current
        ? agingDetailsServerGridRef.current.GetAllGridData()
        : null;

      if (gridData && gridData.fieldTotals) {
        let fieldTotals = gridData.fieldTotals;

        let options = {
          aggregateData: fieldTotals,
        };

        //NOTE: Okay so for some reason this is necessary
        let modifiedProps = {
          ...props,
        };
        modifiedProps.field = props.field.toLowerCase();

        return AggregateMoneyFooterCell(modifiedProps, options);
      }
    }
  };

  let CustomIgnoreDetailCell = (props) => {
    return (
      <td
        aria-colindex={props.tdProps["aria-colindex"]}
        className={props.tdProps.className}
        colSpan={props.tdProps.colSpan}
        role={props.tdProps.role}
        key={props.tdProps.key}
        style={{
          color: "#015CAB",
        }}
      >
        <a
          style={{
            cursor: "pointer",
          }}
          onClick={() => ignoreDetailCellClick(props)}
        >
          Ignore
        </a>
      </td>
    );
  };

  //NOTE: This is the NEW Ignore detail for the Kendo grid
  function ignoreDetailCellClick(props) {
    let dataRow = props.dataItem;

    setIgnoreMultipleSelectedClaimsInDetailModal(false);
    setSelectedNcpdpIgnoreModalText(dataRow.ncpdp);
    setSelectedRxIgnoreModalText(dataRow.rxNumber);
    setSelectedClid(dataRow.claimId);
    setShowIgnoreModal(true);
  }

  function handleIgnoreMultipleSelectedClaims() {
    const allDetailsClaims = agingDetailsServerGridRef.current
      ? agingDetailsServerGridRef.current.GetAllGridData()
      : null;

    let selectedDetailsCount = getSelectedDetailsCount();
    let detailsCount = allDetailsClaims.length;

    if (
      getSelectedDetailsCount() > 14 ||
      selectedDetailsCount === detailsCount
    ) {
      setShowIgnoreCount(true);
    } else {
      setShowIgnoreModal(true);
    }
    setIgnoreMultipleSelectedClaimsInDetailModal(true);
    setSelectedNcpdpIgnoreModalText("All Selected");
    setSelectedRxIgnoreModalText("All Selected");
  }

  function getSelectedDetailsCount() {
    const selectedClaims = agingDetailsServerGridRef.current
      ? agingDetailsServerGridRef.current.GetSelectedGridDataKeys()
      : null;

    if (selectedClaims) {
      let selectedDetailsCount = selectedClaims.length;
      return selectedDetailsCount;
    }

    return 0;
  }

  function handleIgnoreCountClick() {
    setShowIgnoreCount(false);
    setShowIgnoreModal(true);
  }

  function handleCancelIgnoreClick() {
    setShowIgnoreModal(false);
  }

  const onIgnoreClaimsSaveButtonCallback = () => {
    setIsLoading(true);
  };

  const onIgnoreClaimSuccessCallback = (response) => {
    setShowIgnoreModal(false);
    setRefreshDetailGrid(refreshDetailGrid ? false : true);

    invalidateReactQueryKey("api/Aging/GetDateRangeAgingSummary");
    invalidateReactQueryKey("api/AgingDetails/GetByDateRangeAgingServerGrid");

    setIgnoreActionCompletedMessage();
    setIsLoading(false);
  };

  let clidsToIgnore = "";

  if (ignoreMultipleSelectedClaimsInDetailModal) {
    const selectedClaims = agingDetailsServerGridRef.current
      ? agingDetailsServerGridRef.current.GetSelectedGridDataKeys()
      : null;

    if (selectedClaims) {
      clidsToIgnore = selectedClaims.join(",");
    } else {
      clidsToIgnore = "";
    }
  } else {
    clidsToIgnore = selectedClid;
  }

  function setIgnoreActionCompletedMessage() {
    setDetailError({
      status: 200,
      Message: "Selected items have been queued to be ignored!",
    });
    setTimeout(() => {
      setDetailError({});
    }, 3600);
  }

  //set the grid buttons
  useEffect(() => {
    AddGridButton(
      "Ignore Selected Claims",
      handleIgnoreMultipleSelectedClaims,
      true
    );
  }, []);

  const dateRangeAgingApiParams = useMemo(() => {
    if (IsNullOrEmpty(user)) {
      return {};
    }

    if (IsNullOrEmpty(submittedFormData.mmid)) {
      return {};
    }

    let params = {
      user: user,
      mmids: submittedFormData.mmid,
      dateType: submittedFormData.dateType,
      fromDate: submittedFormData.fromDate,
      toDate: submittedFormData.toDate,
      reportType: submittedFormData.reportType,
      includeClaimsWithNoPayer: submittedFormData.includeClaimsWithNoPayer,
      showPayerType: submittedFormData.showPayerType || "", // IRA-38: Include filter parameter
    };

    return params;
  }, [submittedFormData]);

  function handleFormSubmit(e) {
    // Clear any existing error message when initiating a new search
    setError({});

    setSubmittedFormData(e);
    InitiateGridDataStateReset();
  }

  function onFailure(error) {
    console.log(error);
    switch (error.response.config.url || error.config.url) {
      case "api/Aging/GetDateRangeAgingSummary":
        setError(error.response);
        break;
      case "api/AgingDetails/GetByDateRangeAging":
        setDetailError(error.response);
        setShowModal(true);
        break;
      case "api/AgingDetails/IgnoreClaims":
        setIgnoreError(error.response);
        setShowIgnoreModal(true);
        break;
      default:
        setError(error.response);
        break;
    }

    setIsLoading(false);
    return;
  }

  function presentGridData(data) {
    if (data.length > 0) {
      setError({});
    } else {
      setError({ status: 201, Message: "No data found for given parameters" });
    }
  }

  let { mutate: mutateForServerSideExport } = useInsertServerSideExport();

  function detailGridErrorCallback(input) {
    if (detailsGridParameters.isAggregate) {
      let mutateParameter = {
        error: input,
        endpoint:
          "api/AgingDetails/PostAggregateDateRangeAgingDetailsExcelExportRequestToAzureStorage",
      };

      mutateForServerSideExport(mutateParameter);
    } else {
      let mutateParameter = {
        error: input,
        endpoint:
          "api/AgingDetails/PostDateRangeAgingDetailsExcelExportRequestToAzureStorage",
      };

      mutateForServerSideExport(mutateParameter);
    }
  }

  let detailsGridUseQueryOptions = { refetchOnWindowFocus: false };

  return (
    <div className="feature-tab" id="Date-Range">
      {/* <LoadingSpinner
        isDataLoading={isLoading}
        controlsName={"agingDateRange"}
      /> */}
      <ErrorHandler
        error={error}
        onClose={() => {
          setError({});
        }}
      />
      <Accordion defaultExpanded label="Search & Filter">
        <Row>
          <Col className="higher-zindex-filters">
            <DateRangeForm
              handleFormSubmit={handleFormSubmit}
              showFilter={showFilter}
              onShowFilterChange={setShowFilter}
            />
          </Col>
        </Row>
      </Accordion>

      <div ref={_container}>
        <Row>
          <Col md={10} className="tight-grid multi-line-filter">
            <SharedMainSelectableClientGrid
              ref={clientGridRef}
              apiGetEndpoint={"api/Aging/GetDateRangeAgingSummary"}
              apiParameters={dateRangeAgingApiParams}
              aggregateColumnSettings={[
                { aggregate: "sum", field: "Adjudicated" },
                { aggregate: "sum", field: "RxCount" },
                { aggregate: "sum", field: "Paid" },
                { aggregate: "sum", field: "TotalOutstanding" },
              ]}
              sendAggregatesParentCallback={sendAggregatesToParent}
              styleOverride={{ minHeight: "19em", maxHeight: "50em" }}
              exportFileNamePrefix={"DateRangeAging"}
              gridDataStateResetProcessingInitiated={
                gridDataStateResetProcessingInitiated
              }
              onGridDataStateResetCompleted={onGridDataStateResetCompleted}
              onDataStateChangedCallback={() => {
                const gridFilteredData = clientGridRef.current
                  ? clientGridRef.current.GetAllGridData()
                  : [];

                if (gridFilteredData) {
                  presentGridData(gridFilteredData);
                }
              }}
              enableExportAggregates={true}
            >
              <GridColumn
                key={"Payer"}
                field={"Payer"}
                title={"Payer"}
                width={DefaultColumnWidth()}
              />
              <GridColumn
                key={"RxCount"}
                field={"RxCount"}
                title={"Rx Count"}
                filter={"numeric"}
                width={DefaultColumnWidth()}
                cells={{
                  data: RxCountCell,
                  footerCell: AggregateMainGridNumberFooterCell,
                }}
                footerCell={AggregateMainGridNumberFooterCell}
              />
              <GridColumn
                key={"Adjudicated"}
                field={"Adjudicated"}
                title={"Adjudicated"}
                filter={"numeric"}
                width={DefaultColumnWidth()}
                cells={{
                  data: MoneyCell,
                  footerCell: AggregateMainGridMoneyFooterCell,
                }}
                footerCell={AggregateMainGridMoneyFooterCell}
              />
              <GridColumn
                key={"Paid"}
                field={"Paid"}
                title={"Paid"}
                filter={"numeric"}
                width={DefaultColumnWidth()}
                cells={{
                  data: MoneyCell,
                  footerCell: AggregateMainGridMoneyFooterCell,
                }}
                footerCell={AggregateMainGridMoneyFooterCell}
              />
              <GridColumn
                key={"TotalOutstanding"}
                field={"TotalOutstanding"}
                title={"Outstanding"}
                filter={"numeric"}
                width={DefaultColumnWidth()}
                cells={{
                  data: TotalOutstandingMoneyCell,
                  footerCell: AggregateMainGridMoneyFooterCellWithEvent,
                }}
                footerCell={AggregateMainGridMoneyFooterCellWithEvent}
              />
            </SharedMainSelectableClientGrid>
          </Col>
        </Row>
      </div>

      <DetailsModal
        title="Payer Summary"
        show={ShouldDisplayDetailsModal(showModal, showIgnoreModal)}
        handleClose={() => {
          return (
            setShowModal(false),
            setSelectedDetailsState({}),
            setSummaryDetailsGridData(null),
            _container.current.focus()
          );
        }}
      >
        <ErrorHandler
          error={detailError}
          onClose={() => {
            setDetailError({});
          }}
        />
        {summaryDetailsGridData ? (
          <Row>
            <Col className="tight-grid multi-line-filter">
              <Grid
                style={{
                  maxHeight: "10em",
                }}
                data={summaryDetailsGridData}
              >
                <GridColumn
                  key={"ClaimCount"}
                  field={"ClaimCount"}
                  title={"Claim Count"}
                  width={DefaultColumnWidth()}
                />
                <GridColumn
                  key={"Adjudicated"}
                  field={"Adjudicated"}
                  title={"Adjudicated"}
                  width={DefaultColumnWidth()}
                  cells={{ data: MoneyCell }}
                />
                <GridColumn
                  key={"Paid"}
                  field={"Paid"}
                  title={"Paid"}
                  width={DefaultColumnWidth()}
                  cells={{ data: MoneyCell }}
                />
                <GridColumn
                  key={"Outstanding"}
                  field={"Outstanding"}
                  title={"Outstanding"}
                  width={DefaultColumnWidth()}
                  cells={{ data: MoneyCell }}
                />
              </Grid>
            </Col>
          </Row>
        ) : null}
        <br />
        <h2>Aging Detail</h2>

        <h4>{detailsGridTitle}</h4>

        <ZIndexContext.Provider value={10003}>
          <Row>
            <Col className="tight-grid multi-line-filter">
              <SharedMainSelectableServerGrid
                ref={agingDetailsServerGridRef}
                dataItemKey={DETAILS_DATA_ITEM_KEY}
                selectedField={DETAILS_SELECTED_FIELD}
                aggregateColumnSettings={null}
                sendAggregatesParentCallback={() => {}}
                sendSelectablesParentCallback={() => {}}
                exportFileNamePrefix={"DateRangeAgingDetails"}
                apiGetEndpoint={
                  "api/AgingDetails/GetByDateRangeAgingServerGrid"
                }
                apiSubmittedFormData={detailsGridParameters}
                gridErrorHandlerCallback={detailGridErrorCallback}
                secondsBeforeLoadingSpinner={5}
                refreshGrid={refreshDetailGrid}
                useQueryOptions={detailsGridUseQueryOptions}
                gridButtons={gridButtons}
                isSelectable={true}
                onGridApiDataLoadedCallback={() => {
                  SetSummaryDetailsDataHelper(
                    agingDetailsServerGridRef,
                    setSummaryDetailsGridData
                  );
                }}
              >
                {/* <GridToolbar>
                  <button
                    title="Ignore Selected Claims"
                    className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                    onClick={handleIgnoreMultipleSelectedClaims}
                  >
                    Ignore Selected Claims
                  </button>
                </GridToolbar>
                <GridColumn
                  width="30px"
                  filterable={false}
                  headerSelectionValue={
                    serverGridData.findIndex(
                      (item) => !selectedDetailsState[idGetter(item)]
                    ) === -1
                  }
                  headerCell={() => (
                    <input
                      type="checkbox"
                      checked={
                        serverGridData.findIndex(
                          (item) => !selectedDetailsState[idGetter(item)]
                        ) === -1
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const newSelectedState = {};
                        serverGridData.forEach((item) => {
                          newSelectedState[idGetter(item)] = checked;
                        });
                        setSelectedDetailsState(newSelectedState);
                      }}
                    />
                  )}
                  cell={(props) => (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedDetailsState[idGetter(props.dataItem)]}
                        onChange={(e) => {
                          NewDetailSelectionChange(
                            e,
                            props.dataItem[DETAILS_DATA_ITEM_KEY]
                          );
                        }}
                      />
                    </td>
                  )}
                /> */}
                <GridColumn
                  key={""}
                  field={""}
                  title={"Ignore"}
                  filterable={false}
                  width={"100em"}
                  cells={{
                    data: CustomIgnoreDetailCell,
                  }}
                />
                <GridColumn
                  key={"ncpdp"}
                  field={"ncpdp"}
                  title={"NCPDP"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                />
                <GridColumn
                  key={"rxNumber"}
                  field={"rxNumber"}
                  title={"Rx #"}
                  filter={"text"}
                  width={DefaultColumnWidth()}
                />
                <GridColumn
                  key={"dispensedDate"}
                  field={"dispensedDate"}
                  title={"Dispensed Date"}
                  filter={"date"}
                  format="{0:d}"
                  width={DefaultDateColumnWidth()}
                />
                <GridColumn
                  key={"transactionDate"}
                  field={"transactionDate"}
                  title={"Transaction Date"}
                  filter={"date"}
                  format="{0:d}"
                  width={DefaultDateColumnWidth()}
                />
                {detailsGridParameters.isAggregate ? (
                  <GridColumn
                    key={"payerName"}
                    field={"payerName"}
                    title={"Payer Name"}
                    width={DefaultColumnWidth()}
                  />
                ) : null}
                <GridColumn
                  key={"bin"}
                  field={"bin"}
                  title={"BIN"}
                  width={DefaultColumnWidth()}
                />
                <GridColumn
                  key={"pcn"}
                  field={"pcn"}
                  title={"PCN"}
                  width={DefaultColumnWidth()}
                />
                <GridColumn
                  key={"promiseToPay"}
                  field={"promiseToPay"}
                  title={"Promise to Pay"}
                  filter={"numeric"}
                  width={DefaultColumnWidth()}
                  cells={{
                    data: MoneyCell,
                    footerCell: AggregateMoneyDetailFooterCell,
                  }}
                  footerCell={AggregateMoneyDetailFooterCell}
                  headerCell={PromiseToPayHeaderCell}
                />
                <GridColumn
                  key={"remittancePaid"}
                  field={"remittancePaid"}
                  title={"Paid"}
                  filter={"numeric"}
                  width={DefaultColumnWidth()}
                  cells={{
                    data: MoneyCell,
                    footerCell: AggregateMoneyDetailFooterCell,
                  }}
                  footerCell={AggregateMoneyDetailFooterCell}
                />
                <GridColumn
                  key={"outstanding"}
                  field={"outstanding"}
                  title={"Outstanding"}
                  filter={"numeric"}
                  width={DefaultColumnWidth()}
                  cells={{
                    data: MoneyCell,
                    footerCell: AggregateMoneyDetailFooterCell,
                  }}
                  footerCell={AggregateMoneyDetailFooterCell}
                />
                <GridColumn
                  key={"claimNumber"}
                  field={"claimNumber"}
                  title={"Claim #"}
                  width={DefaultColumnWidth()}
                />
                <GridColumn
                  key={"cardholderId"}
                  field={"cardholderId"}
                  title={"Cardholder ID"}
                  width={DefaultColumnWidth()}
                />
                <GridColumn
                  key={"adjudicatedCoPay"}
                  field={"adjudicatedCoPay"}
                  title={"Copay"}
                  filter={"numeric"}
                  width={DefaultColumnWidth()}
                  cells={{
                    data: MoneyCell,
                    footerCell: AggregateMoneyDetailFooterCell,
                  }}
                  footerCell={AggregateMoneyDetailFooterCell}
                />
              </SharedMainSelectableServerGrid>
            </Col>
          </Row>
        </ZIndexContext.Provider>
      </DetailsModal>
      <IgnoreModal
        title="Ignore Count"
        show={showIgnoreCount}
        handleClose={() => {
          setShowIgnoreCount(false);
        }}
      >
        <Container>
          <Row>
            You have selected to ignore {getSelectedDetailsCount()} claims. Do
            you wish to continue?
          </Row>

          <br />
          <Row>
            <Col>
              <Button
                variant="secondary"
                onClick={() => setShowIgnoreCount(false)}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                className="primary-button"
                onClick={handleIgnoreCountClick}
              >
                Ok
              </Button>
            </Col>
          </Row>
        </Container>
      </IgnoreModal>
      <IgnoreModal
        title="Ignore Details"
        show={showIgnoreModal}
        handleClose={() => {
          return (
            setShowIgnoreModal(false),
            setIgnoreMultipleSelectedClaimsInDetailModal(false)
          );
        }}
      >
        <ErrorHandler
          error={ignoreError}
          onClose={() => {
            setIgnoreError({});
          }}
        />
        <IgnoreBody
          selectedNcpdpIgnoreModalText={selectedNcpdpIgnoreModalText}
          selectedRxIgnoreModalText={selectedRxIgnoreModalText}
          handleCancelClick={handleCancelIgnoreClick}
          selectedClidsToIgnore={clidsToIgnore}
          onSaveButtonClickCallbackFromParent={onIgnoreClaimsSaveButtonCallback}
          onSuccessCallbackFromParent={onIgnoreClaimSuccessCallback}
          disableControls={isLoading}
        ></IgnoreBody>
      </IgnoreModal>
    </div>
  );
}
